import { LayoutItem } from 'api/models';
import AddFieldButton from './AddFieldButton';
import FieldTemplateControls from './FieldTemplateControls';
import FieldRenderer from './FieldRenderer';
import { Card } from '@mui/material';
import { LayoutItemSubType, LayoutItemType } from 'api/models/FormTemplate';
import { DividerField } from './Fields/DividerField';
import SelectableTable from './Fields/SelectableTable/SelectableTable';
import { LayoutField } from './Fields';
import { memo, useEffect, useRef } from 'react';
import { DropArea } from './DropArea';
import { FormTemplateEditorMode } from 'routes/FormTemplateEditor/FormTemplateEditor';
import { useRecoilValue } from 'recoil';
import { lastAddedLayoutItem } from 'state';
import { isElementFullyVisible } from '../utils';

interface FieldTemplateRendererProps {
  disabled?: boolean;
  layoutItem: LayoutItem;
  parentPath: string;
  displayAddButton?: boolean;
  index: number;
  mode?: FormTemplateEditorMode;
}

function FieldTemplateRenderer({
  disabled,
  layoutItem,
  parentPath,
  displayAddButton = true,
  index,
  mode,
}: FieldTemplateRendererProps) {
  const latestAddedItem = useRecoilValue(lastAddedLayoutItem);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (
      ref.current &&
      !isElementFullyVisible(ref.current) &&
      latestAddedItem &&
      latestAddedItem.id === layoutItem.id
    ) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [latestAddedItem, layoutItem.id]);

  const renderLayoutItems = () => {
    switch (layoutItem.type) {
      case LayoutItemType.Container:
        if (layoutItem.subType === LayoutItemSubType.Layout) {
          return (
            <LayoutField
              parentPath={parentPath}
              editDisabled={
                disabled ||
                (mode === FormTemplateEditorMode.sample && index === 0)
              }
              index={index}
            />
          );
        } else if (layoutItem.subType === LayoutItemSubType.SelectableTable) {
          return (
            <SelectableTable
              parentPath={parentPath}
              disabled={disabled}
              index={index}
            />
          );
        }
        break;
      case LayoutItemType.Input:
        if (layoutItem.fieldTemplateId) {
          return <FieldRenderer fieldId={layoutItem.fieldTemplateId} />;
        }
        break;
      case LayoutItemType.Static:
        return <DividerField layoutItem={layoutItem} />;
      default:
        throw new Error('Unknown layout item type');
    }
  };

  return (
    <>
      <Card
        ref={ref}
        elevation={1}
        sx={{
          p: 1,
          border: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <FieldTemplateControls
          layoutItem={layoutItem}
          disabled={disabled}
          fieldId={layoutItem.fieldTemplateId!}
          parentPath={parentPath}
          index={index}
        />
        {renderLayoutItems()}
      </Card>
      {displayAddButton && (
        <AddFieldButton
          disabled={disabled}
          sx={{ mt: 1, mb: 1 }}
          parentPath={parentPath}
          insertIndex={index + 1}
        />
      )}
      <DropArea index={index + 1} parentPath={parentPath} />
    </>
  );
}

export default memo(FieldTemplateRenderer);
